/* Material */
import Typo from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { useDiscountDateActive } from 'src/hooks/useDiscountDateActive'

/* Types */
import { discountPrice } from 'src/shared/frontendTypes/product'
import { TypographyProps } from '@mui/material'
import QuantityDiscountInfo from './QuantityDiscountInfo'
import { getQuantityDiscountText } from 'src/functions/quantityDiscountText'
import type { QuantityDiscountText } from 'src/functions/quantityDiscountText'

/* Next-Dynamic */
import dynamic from 'next/dynamic'
import type { CountdownClockDisplayVariant } from 'src/components/CountdownClock/TextClock'
const CountdownClock = dynamic(() => import('src/components/CountdownClock/TextClock'), {
  ssr: false,
})

type CountdownClockProps = Omit<TypographyProps, 'component' | 'variant'> & {
  component?: TypographyProps['component']
  displayVariant?: CountdownClockDisplayVariant
  variant?: 'default' | 'alternative' | 'classic' | CountdownClockDisplayVariant | TypographyProps['variant']
}

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    title: {
      '&.small': {
        fontSize: '1.2rem',
      },
    },
    quantityDiscount: {
      marginTop: _theme.spacing(.5),
    },
  })
)

interface Props extends Omit<TypographyProps, 'component'> {
  discount: discountPrice
  qty?: number
  className?: string
  component?: TypographyProps['component']
  clock?: CountdownClockProps
  size?: 'small' | 'medium' | 'large'
  showQuantityDiscount?: boolean
  quantityDiscountText?: QuantityDiscountText
}

const DiscountInfos: React.FC<Props> = ({
  discount,
  qty,
  className, clock,
  size = 'medium',
  showQuantityDiscount = true,
  quantityDiscountText: quantityDiscountTextOverride,
  ...rest
}) => {

  const classes = useStyles()
  const { title, type, dateEnd } = discount
  const discountDateActive = useDiscountDateActive(dateEnd)
  if (!discountDateActive) return null

  const quantityDiscountText = showQuantityDiscount
    && discount.showPercent !== false
    ? quantityDiscountTextOverride || getQuantityDiscountText(discount.quantityDiscount, qty)
    : undefined
  const hasBasePriceDiscount = discount.discountPrice < discount.regularPrice - 0.005
    || (
      typeof discount.discountPriceAfterStock === 'number'
      && discount.discountPriceAfterStock < discount.regularPrice - 0.005
    )
  const titleHidden = [
    'invisible',
    'crossed',
  ].includes(type) || (!hasBasePriceDiscount && !quantityDiscountText)

  if (titleHidden && !quantityDiscountText) return null // Still oder nur Streichpreis

  return <div className={className+' '+size}>

    {!titleHidden && (
      <Typo {...rest} className={classes.title+' '+size}>
        {title}
      </Typo>
    )}

    {quantityDiscountText && (
      <QuantityDiscountInfo
        text={quantityDiscountText}
        dateEnd={dateEnd}
        className={classes.quantityDiscount}
        component='div'
        variant={size === 'small' ? 'body2' : 'body1'}
        align={rest.align}
      />
    )}

    { type === 'countdown' && dateEnd &&
      <CountdownClock
        {...{ dateEnd, size }}
        displayVariant={discount.countdownVariant ?? undefined}
        {...clock}
      />
    }

  </div>
 
}

export default DiscountInfos
